import { lazy, Suspense, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Spinner } from "@astrum/ui";
import { useAuth } from "../lib/auth.js";
import "./emoji-picker.css";

/* The picker and its dataset are a separate chunk, fetched the first time
 * somebody opens one. */
const EmojiPicker = lazy(() => import("./EmojiPicker.js"));

export interface EmojiPickerPopoverProps {
  open: boolean;
  onClose: () => void;
  onPick: (emoji: string) => void;
  /** The button that opens it; the panel is positioned against this. */
  trigger: ReactNode;
  label: string;
  /** Open toward the left, for a trigger near the right edge. */
  alignRight?: boolean;
  /** Open downward, for a trigger near the top of a scroller. */
  below?: boolean;
}

export function EmojiPickerPopover({ open, onClose, onPick, trigger, label, alignRight, below }: EmojiPickerPopoverProps) {
  const { viewer } = useAuth();
  const wrapRef = useRef<HTMLDivElement>(null);

  // Click outside closes it; without that it is a toggle that traps the user.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open, onClose]);

  return (
    <div className="emoji-popover" ref={wrapRef}>
      {trigger}
      {open && (
        <div className={`emoji-popover__panel ${alignRight ? "is-right" : ""} ${below ? "is-below" : ""}`}>
          <Suspense
            fallback={
              <div className="emoji-picker">
                <div className="emoji-picker__loading">
                  <Spinner size="sm" label="Loading emoji" />
                </div>
              </div>
            }
          >
            <EmojiPicker label={label} userId={viewer?.id} onClose={onClose} onPick={onPick} />
          </Suspense>
        </div>
      )}
    </div>
  );
}
